import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { SreenCoverLoading } from "src/components";
import { useAlert, useUserStepStatusYear } from "src/hooks";
import { recoilStates } from "src/recoil/recoilStates";
import styled from "styled-components";
import Step1RegistrantInfo from "./YearGroupRegisterSteps/Step1RegistrantInfo";
import Step2RequiredInfo from "./YearGroupRegisterSteps/Step2RequiredInfo";
import Step3AdditionalInfo from "./YearGroupRegisterSteps/Step3AdditionalInfo";

export default function YearGroupRegister() {
  const [user] = useRecoilState(recoilStates.user);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // user 정보가 있을 때 초기값으로만 설정 (최초 한 번만)
  const [hasInitialized, setHasInitialized] = useState(false);
  
  useEffect(() => {
    // 이미 초기화했으면 더 이상 자동 설정하지 않음
    if (hasInitialized) return;
    
    // user 정보를 trim 처리하고 유효한 값만 확인
    const trimmedRealname = user?.realname?.trim();
    const trimmedPhone = user?.phone?.trim();
    
    // user 정보가 있을 때는 초기값으로 설정
    if (trimmedRealname || trimmedPhone) {
      setFormData((prev: any) => ({
        ...prev,
        proposerNameValue: trimmedRealname || prev.proposerNameValue || "",
        contactNumberValue: trimmedPhone || prev.contactNumberValue || "",
      }));
    }
    
    // user 정보가 있든 없든 초기화 완료로 표시 (사용자가 입력할 수 있도록)
    setHasInitialized(true);
  }, [user, hasInitialized]);

  const userStep = useUserStepStatusYear();
  const alert = useAlert();
  const [count, setCount] = useState(0);
  useEffect(() => {
    (async () => {
      if (!userStep) {
        return;
      }
      if (count > 1) {
        return;
      }
      if (userStep !== "noGroup") {
        alert({
          content: "이미 그룹을 만들었습니다.",
          onClose: () => {
            alert.resume();
          },
        });
        await alert.pause();
        alert.resume();
        navigate("/");
      }
    })();
    setCount((c) => c + 1);
  }, [userStep]);

  const handleStep1Next = (step1Data: {
    proposerNameValue: string;
    contactNumberValue: string;
  }) => {
    setFormData((prev: any) => ({ ...prev, ...step1Data }));
    setCurrentStep(2);
  };

  const handleStep2Next = (step2Data: {
    groupNameValue: string;
    memberCountValue: string;
    activeGroupTypeId: string;
    activeDistrictId: string;
  }) => {
    setFormData((prev: any) => ({ ...prev, ...step2Data }));
    setCurrentStep(3);
  };

  const handleStep2Prev = () => {
    setCurrentStep(1);
  };

  const handleStep3Prev = () => {
    setCurrentStep(2);
  };

  const handleLoadingChange = (isLoading: boolean) => {
    setLoading(isLoading);
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "등록자 정보";
      case 2:
        return "단체 필수 정보";
      case 3:
        return "단체 부가 정보";
      default:
        return "등록자 정보";
    }
  };

  const getProgressWidth = () => {
    return (currentStep / 2) * 100;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1RegistrantInfo onNext={handleStep1Next} formData={formData} />;
      case 2:
        return (
          <Step2RequiredInfo
            onNext={handleStep2Next}
            onPrev={handleStep2Prev}
            formData={formData}
          />
        );
      case 3:
        return (
          <Step3AdditionalInfo
            onPrev={handleStep3Prev}
            formData={formData}
            onLoadingChange={handleLoadingChange}
          />
        );
      default:
        return <Step1RegistrantInfo onNext={handleStep1Next} />;
    }
  };

  return (
    <>
      <Container>
        {/* Progress Bar */}
        <ProgressContainer>
          <ProgressBackground />
          <ProgressFill width={`${getProgressWidth()}%`} />
        </ProgressContainer>

        {/* Main Content */}
        <MainContent>{renderStep()}</MainContent>
      </Container>
      {loading && <SreenCoverLoading />}
    </>
  );
}

const Container = styled.div`
  width: 100%;
  height: 844px;
  background: #fdfdfd;
  position: relative;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0 auto;
`;

const ProgressContainer = styled.div`
  width: 100%;
  height: 4px;
  position: absolute;
  left: 0px;
  top: 0;
`;

const ProgressBackground = styled.div`
  width: 100%;
  height: 4px;
  background: #d1d5d8;
  position: absolute;
  left: 0px;
  top: 0px;
`;

const ProgressFill = styled.div<{ width: string }>`
  width: ${(props) => props.width};
  height: 4px;
  background: #007bff;
  position: absolute;
  left: 0px;
  top: 0px;
  transition: width 0.3s ease;
`;

const MainContent = styled.div``;
